import { memo } from 'react';
import classNames from 'classnames';
import InputTitleLabel from 'components/inputTitleLabel';
import BaseTextInput from 'components/inputText/baseTextInput';
import { useDeviceSize } from 'hooks/useDeviceSize';

export default memo(
  ({
    title = '',
    formik,
    required = false,
    fieldName,
    value = null,
    placeholder = '入力してください',
    suffix = '',
    fields = null,
    disabled = false,
    numberOnly = false,
    className = { boxClass: '', titleClass: '', inputClass: '' },
    isTitleVerticalPosition = false,
    isShowErrorArea = true,
    errorMessage = null,
    isLongError = false,
    isErrorAreaAbsolute = false,
    type,
    ...rest
  }) => {
    const { isTablet, isSp } = useDeviceSize();

    const isVertical = isSp || (isTablet && isTitleVerticalPosition);

    const _fields = fields || [
      { fieldName, placeholder, value, suffix, ...rest },
    ];
    const isSuffix = _fields?.[_fields.length - 1]?.suffix;

    const createErrorMessage = field =>
      errorMessage != null
        ? errorMessage
        : (!disabled &&
            formik &&
            formik?.touched[field] &&
            formik?.errors[field]) ||
          '';

    const createErrorMessageSummary = () =>
      _fields?.map(f => createErrorMessage(f.fieldName))?.filter(f => f)?.[0] ??
      '';

    return (
      <div className={classNames('w-full', className.boxClass)}>
        {/* タイトルとエラーエリアの配置 */}
        <div
          className={classNames(
            'flex flex-col gap-y-1',
            isTitleVerticalPosition ? 'lg:flex-row' : 'sm:flex-row',
            isErrorAreaAbsolute ? 'relative' : ''
          )}
        >
          {!isVertical ? (
            <div className="flex flex-col justify-center">
              {isShowErrorArea && <div className="min-h-[42px]"></div>}
              <InputTitleLabel
                title={title}
                required={required}
                disabled={disabled}
                className={classNames(
                  className.titleClass,
                  'mb-1 sm:mb-0',
                  'mr-0 sm:mr-2'
                )}
              />
            </div>
          ) : (
            <div
              className={classNames('flex flex-row items-end justify-between')}
            >
              <InputTitleLabel
                title={title}
                required={required}
                disabled={disabled}
                className={classNames(
                  className.titleClass,
                  'mb-1 sm:mb-0',
                  'mr-0 sm:mr-2'
                )}
              />
              {isShowErrorArea && (
                <label
                  className={classNames(
                    'text-sm w-auto text-red',
                    isSuffix ? 'mr-6' : '',
                    createErrorMessage(fieldName)?.length >= 16
                      ? 'whitespace-pre-wrap break-words'
                      : 'whitespace-nowrap',
                    isErrorAreaAbsolute && 'absolute right-0'
                  )}
                >
                  {createErrorMessageSummary()}
                </label>
              )}
            </div>
          )}
          <div
            className={classNames(
              'flex w-full gap-x-2 sm:gap-x-4',
              isTitleVerticalPosition ? 'lg:mt-0' : 'sm:mt-0'
            )}
          >
            {_fields.map((f, index) => {
              const { fieldName, placeholder, value, suffix, ...frest } = f;
              return (
                <div
                  key={fieldName || index}
                  className="flex w-full flex-col gap-y-1"
                >
                  {isShowErrorArea && !isVertical && (
                    <div
                      className={classNames(
                        'flex flex-row items-end justify-between',
                        !isErrorAreaAbsolute && isShowErrorArea
                          ? 'h-[42px]'
                          : '',
                        isErrorAreaAbsolute ? 'relative' : ''
                      )}
                    >
                      <label
                        className={classNames(
                          'text-sm w-auto text-red',
                          'max-w-full',
                          'overflow-hidden',
                          'whitespace-pre-wrap',
                          'min-h-[24px]',
                          isSuffix ? 'mr-6' : '',
                          isSp
                            ? 'w-full max-w-full text-left'
                            : 'max-w-[180px] sm:max-w-[260px] lg:max-w-[320px]',
                          'max-h-[50px] min-h-[24px]',
                          createErrorMessage(fieldName)?.length >= 16
                            ? 'whitespace-pre-wrap break-words'
                            : 'whitespace-nowrap',
                          isErrorAreaAbsolute && !isSp ? 'absolute right-0' : ''
                        )}
                      >
                        {createErrorMessage(fieldName)}
                      </label>
                    </div>
                  )}
                  <span className="relative flex w-full">
                    <BaseTextInput
                      formik={formik}
                      fieldName={fieldName}
                      placeholder={placeholder}
                      value={value}
                      disabled={disabled}
                      numberOnly={numberOnly}
                      error={!!createErrorMessage(fieldName)}
                      type={type}
                      className={classNames(className.inputClass)}
                      {...frest}
                    />
                    {suffix && (
                      <label
                        className={classNames(
                          'ml-2 self-center',
                          'min-w-[1.5rem]',
                          disabled ? 'text-disabled' : 'text-navy'
                        )}
                      >
                        {suffix}
                      </label>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);
